/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import BaseSummarizerQuery from './BaseSummarizerQuery';

/**
 * The FilteredSummaryValueQuery is a single query that updates summaryCollector entries
 * with the single row result of the aggregated summary values with filtering applied
 * for all measures (ordinals) that are projected.
 * Currently this query is only done to compute values for countdistinct.
 * Logic to process exception conditions to avoid querying and update summaries immediately are done
 * NOTE: This query considers selections but does not query for single selections.
 **/
class FilteredSummaryValueQuery extends BaseSummarizerQuery {

	/**
	 * @Constructor
	 * @param {Object} widgetApi - the owner widget (needed to access services)
	 * @param {Object} dashboardApi - the dashboardApi
	 * @param {Object} logger - The logger,
	 * @param {Function} [cbGetRowTuples=null] - when this callback is supplied, the rendered data rows are used
	 *				where the filter values are "implied" (eg TopN).  This is only needed for summary stats (not selection stats)
	 */
	constructor(widgetApi, dashboardApi, logger, cbGetRowTuples=null) {
		super(widgetApi, dashboardApi, logger, cbGetRowTuples);
		this._unprocessedItems = [];	//Keep a list of facts that this query couldn't process.
	}

	/**
	 * Add an item to the filteredSummaryValue query definition UNLESS
	 * 1) it is not a fact or an aggregation type we're interested (currently only countdistinct is processed)
	 * 2) if this is a selection with only 1 value, the value is the same as the summary, add it to the collector instead.
	 * @param {object} queryDef - the queryDefinition that will be modified with this item if it applies
	 * @param {object[]} queryDef.dataItems - simple data item _querySpec
	 * @param {String[]} queryDef.projections - the list of projected items (ids same as dataItems list)
	 * @param summaryCollector - a summaryCollector (or selectionSummaryCollector) whose entries are to be augmented with the overallSummary values.
	*/
	addQueryItem(queryDef, mappedDataItem, summaryCollector) {
		if (mappedDataItem.getType()==='fact' && mappedDataItem.getAggregation()==='countdistinct') {
			let itemUpdated=false;

			//If we only have 1 value in this selection summary, we don't need to query.
			if (summaryCollector.isSelectionSummaryCollector) {
				const dataItemUniqueId = mappedDataItem.getId();
				const summaryEntry = summaryCollector.getSummary(dataItemUniqueId);
				const summaryEntryValues = summaryEntry && summaryEntry.valueMap && Object.keys(summaryEntry.valueMap);
				if (summaryEntryValues && summaryEntryValues.length === 1) {
					//We know we have exactly 1 value in the valueMap, so assign it and mark the item as updated.
					this.updateSummaryEntry(summaryCollector, dataItemUniqueId, summaryEntry.valueMap[summaryEntryValues[0]], 'filteredSummaryValueUse', 'filteredSummaryValue');
					itemUpdated=true;
				} else if (!summaryEntryValues) {
					//If there are no selections, remove the entry.
					summaryCollector.removeSummary(dataItemUniqueId);
					itemUpdated=true;
				}
			}
			if (!itemUpdated) {
				const dataItemJSON = {
					aggregate: mappedDataItem.getAggregation(),
					itemId: mappedDataItem.getColumnId(),
					id: mappedDataItem.getId()
				};

				queryDef.dataItems.push(dataItemJSON);
				queryDef.projections.push(mappedDataItem.getId());
			}
		} else if (mappedDataItem.getType()==='fact') {
			this._unprocessedItems.push(mappedDataItem.getAggregation() + mappedDataItem.getColumnId());
		}
	}

	getUnprocessedItems() {
		return this._unprocessedItems;
	}

	/**
	 * Process the value of an item returned from the query by adding it to the summaryCollector
	 * @param {String} dataItemUniqueId - the id of the dataItem.
	 * @param {String} aggregate - the aggregation type (eg countdistinct)
	 * @param {String} filteredSummaryUseValue - the actual value (to be added as the filteredSummaryUseValue)
	 * @param {Object} summaryCollector - the summaryCollector to add the value to.
	 */
	processValue(dataItemUniqueId, aggregate, filteredSummaryUseValue, summaryCollector) {
		this.updateSummaryEntry(summaryCollector, dataItemUniqueId, filteredSummaryUseValue, 'filteredSummaryValueUse', 'filteredSummaryValue');
	}
}

export default FilteredSummaryValueQuery;



// WEBPACK FOOTER //
// ./src/js/lib/@waca/vis-summarizer/src/js/FilteredSummaryValueQuery.js